/**
 * Joint seat — personal AA-clear × healer staff cover.
 * Wall 5 response: dual-seat must not ignore healer reach.
 * Soft pull only — reclaim Soft plateau if aggressive bias re-enters chew.
 */
import { judgmentNorm } from "./traits";
import { dist2d } from "./cover-subject";
import type { RaidUnit } from "../raid-types";
import {
  getJudgmentAssistKnobs,
  jointEnabled,
  seatFilterEnabled,
} from "./knobs";
import { unitIsStaffHealer } from "./hero-registry";

export type JointSeatScore = {
  /** 0..1 — higher = better joint seat. */
  score01: number;
  inHealerStaff: boolean;
  nearestHealerDist: number | null;
};

export function livingHealers(
  allies: readonly RaidUnit[],
): RaidUnit[] {
  return allies.filter((a) => unitIsStaffHealer(a));
}

export function pointInAnyHealerStaff(
  x: number,
  y: number,
  healers: readonly RaidUnit[],
): { inside: boolean; nearestDist: number | null } {
  let nearest: number | null = null;
  let inside = false;
  for (const h of healers) {
    const d = dist2d(x, y, h.x, h.y);
    if (nearest == null || d < nearest) nearest = d;
    if (d < h.attackRange) inside = true;
  }
  return { inside, nearestDist: nearest };
}

/**
 * Score a candidate seat: clear seats preferred; insight pulls toward staff cover.
 * `personalClear01` = 1 when AA-clear, 0 when chewed (caller supplies).
 */
export function scoreJointSeat(
  x: number,
  y: number,
  healers: readonly RaidUnit[],
  insight: number,
  personalClear01: number,
): JointSeatScore {
  const { inside, nearestDist } = pointInAnyHealerStaff(x, y, healers);
  const insightT = judgmentNorm(insight);
  // Soft cover weight — personal clear still dominates.
  const coverW = Math.max(0, Math.min(0.35, 0.1 + 0.25 * Math.max(0, insightT)));
  const cover01 = inside
    ? 1
    : nearestDist == null
      ? 0
      : Math.max(0, 1 - nearestDist / 220);
  const score01 = Math.max(
    0,
    Math.min(1, personalClear01 * (1 - coverW) + cover01 * coverW),
  );
  return { score01, inHealerStaff: inside, nearestHealerDist: nearestDist };
}

/**
 * Additive dual-seat tournament bonus for staff cover (filter, not pull).
 * Caller must already dominate with DZ/packChew clear terms.
 */
export function seatStaffCoverScoreBonus(
  x: number,
  y: number,
  healers: readonly RaidUnit[],
  insight: number,
  personalClear01 = 1,
): number {
  if (!seatFilterEnabled()) return 0;
  const k = getJudgmentAssistKnobs();
  if (k.seatFilterScore <= 0) return 0;
  if (insight < k.jointInsightMin) return 0;
  if (healers.length === 0) return 0;
  const scored = scoreJointSeat(x, y, healers, insight, personalClear01);
  return scored.score01 * k.seatFilterScore;
}

/**
 * Soft-bias an already-chosen escape seat toward healer staff without
 * re-entering packChew (caller must re-validate clear). Insight-scaled.
 */
export function biasSeatTowardHealerCover(
  seat: { tx: number; ty: number },
  healers: readonly RaidUnit[],
  insight: number,
): { tx: number; ty: number; pulled: boolean } {
  if (!jointEnabled()) return { ...seat, pulled: false };
  if (healers.length === 0) return { ...seat, pulled: false };
  const k = getJudgmentAssistKnobs();
  if (insight < k.jointInsightMin) return { ...seat, pulled: false };
  const insightT = judgmentNorm(insight);
  const pull = Math.min(
    k.jointPullMax,
    Math.max(0, 0.04 + 0.12 * Math.max(0, insightT)),
  );
  if (pull < 0.05) return { ...seat, pulled: false };
  const { inside, nearestDist } = pointInAnyHealerStaff(
    seat.tx,
    seat.ty,
    healers,
  );
  if (inside) return { ...seat, pulled: false };
  // Boids lesson: only bias when clearly outside staff with margin.
  if (nearestDist != null && nearestDist < k.jointMinDistPx) {
    return { ...seat, pulled: false };
  }
  let bestH: RaidUnit | null = null;
  let bestD = Infinity;
  for (const h of healers) {
    const d = dist2d(seat.tx, seat.ty, h.x, h.y);
    if (d < bestD) {
      bestD = d;
      bestH = h;
    }
  }
  if (!bestH || nearestDist == null) return { ...seat, pulled: false };
  // Prefer staff lip, not healer feet — reduce chew re-entry risk.
  const sit = Math.max(16, bestH.attackRange * 0.85);
  const dx = seat.tx - bestH.x;
  const dy = seat.ty - bestH.y;
  const len = Math.hypot(dx, dy) || 1;
  const ix = bestH.x + (dx / len) * sit;
  const iy = bestH.y + (dy / len) * sit;
  return {
    tx: seat.tx + (ix - seat.tx) * pull,
    ty: seat.ty + (iy - seat.ty) * pull,
    pulled: true,
  };
}
